import type {
  ReportBucketType,
  ReportQueryParams,
  ReportPeriodPreset,
} from '@services/api/reports/finance.api'
import {
  computeChartAxis,
  formatCurrency,
  formatPercent,
} from '@utils/dashboardFinance.util'

const SNAPSHOT_HOUR_BY_ID: Record<string, number> = {
  '12am': 0,
  '1am': 1,
  '2am': 2,
  '3am': 3,
  '4am': 4,
  '5am': 5,
  '6am': 6,
  '7am': 7,
  '8am': 8,
  '9am': 9,
  '10am': 10,
  '11am': 11,
  '12pm': 12,
  '1pm': 13,
  '2pm': 14,
  '3pm': 15,
  '4pm': 16,
  '5pm': 17,
  '6pm': 18,
  '7pm': 19,
  '8pm': 20,
  '9pm': 21,
  '10pm': 22,
  '11pm': 23,
}

const PERIOD_PRESET_IDS = new Set<string>([
  'this-year',
  'this-month',
  'this-week',
  'today',
  'yesterday',
  'last-year',
  'last-month',
  'last-week',
  'last-90-days',
  'last-60-days',
  'last-30-days',
  'last-7-days',
  'last-9-months',
  'last-6-months',
  'last-3-months',
  'calendar-q1',
  'calendar-q2',
  'calendar-q3',
  'calendar-q4',
  'date-range',
  'snapshot',
])

const ymdToIso = (ymd: string): string => {
  const trimmed = ymd.trim()
  if (!trimmed) return ''
  if (trimmed.includes('T')) return trimmed
  return new Date(`${trimmed}T12:00:00.000Z`).toISOString()
}

export const mapToolbarToFinanceQuery = (
  toolbarSelection: Record<string, string>,
  clinicId: string,
  referenceDate?: string,
): ReportQueryParams | null => {
  if (!clinicId) return null

  const latestData = toolbarSelection['period-category'] ?? 'latest-data'
  const periodChip = toolbarSelection['period-preset'] ?? 'this-month'
  const providerChip = toolbarSelection['provider-filter'] ?? 'all-providers'

  const query: ReportQueryParams = {
    clinicId,
    periodPreset: 'this-month',
    ...(referenceDate ? { referenceDate } : {}),
  }

  // providerChip is a provider_id UUID when a specific provider is selected.
  if (providerChip && providerChip !== 'all-providers') {
    query.providerId = providerChip
  }

  if (latestData === 'snapshot') {
    query.periodPreset = 'snapshot'
    const snapshotYmd = toolbarSelection['snapshot-as-of']?.trim()
    query.snapshotDate = snapshotYmd
      ? ymdToIso(snapshotYmd)
      : new Date().toISOString()
    const timeId = toolbarSelection['snapshot-time'] ?? '6am'
    query.snapshotHour = SNAPSHOT_HOUR_BY_ID[timeId] ?? 6
    return query
  }

  if (periodChip === 'date-range') {
    const start = toolbarSelection['hist-range-start']?.trim()
    const end = toolbarSelection['hist-range-end']?.trim()
    if (!start || !end) return null
    query.periodPreset = 'date-range'
    query.startDate = ymdToIso(start)
    query.endDate = ymdToIso(end)
    return query
  }

  if (PERIOD_PRESET_IDS.has(periodChip)) {
    query.periodPreset = periodChip as ReportPeriodPreset
    return query
  }

  query.periodPreset = 'this-month'
  return query
}

export const formatBucketPeriodLabel = (bucket: ReportBucketType): string => {
  switch (bucket) {
    case 'month':
      return 'Monthly'
    case 'week':
      return 'Weekly'
    case 'day':
      return 'Daily'
    case 'hour':
      return 'Hourly'
    default:
      return 'By Period'
  }
}

export const formatKpiCurrency = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return formatCurrency(value, { compact: true })
}

export const formatKpiPercent = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return formatPercent(value, 1)
}

export type ReportChartAxisConfig = {
  domain: [number, number]
  ticks: number[]
  tickFormatter: (v: number) => string
}

export const computeDynamicDollarAxis = (
  values: number[],
): ReportChartAxisConfig => {
  const axis = computeChartAxis(values)
  return {
    domain: axis.yDomain as [number, number],
    ticks: axis.yTicks,
    tickFormatter: (v: number): string => {
      if (v === 0) return '$0'
      if (v >= 1000) return `$${Math.round(v / 1000)}K`
      return `$${v}`
    },
  }
}

export const computeDynamicCountAxis = (
  values: number[],
): ReportChartAxisConfig => {
  const axis = computeChartAxis(values)
  return {
    domain: axis.yDomain as [number, number],
    ticks: axis.yTicks,
    tickFormatter: (v: number): string => {
      if (Number.isInteger(v)) return v.toLocaleString('en-US')
      return Math.round(v).toLocaleString('en-US')
    },
  }
}

export const computePercentAxis = (values: number[]): ReportChartAxisConfig => {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 100 : Math.ceil((max * 1.1) / 10) * 10
  const step = axisMax <= 50 ? 10 : axisMax <= 100 ? 20 : 25
  const ticks: number[] = []
  for (let v = 0; v <= axisMax; v += step) {
    ticks.push(v)
  }
  if (ticks[ticks.length - 1] !== axisMax) {
    ticks.push(axisMax)
  }
  return {
    domain: [0, axisMax] as [number, number],
    ticks,
    tickFormatter: (v: number): string => `${v}%`,
  }
}

export const mergeProductionCollectionsByPeriod = (
  production: Array<{ label: string; value: number }>,
  collections: Array<{ label: string; value: number }>,
): Array<{ period: string; production: number; collections: number }> => {
  const collectionByLabel = new Map(collections.map((b) => [b.label, b.value]))
  return production.map((row) => ({
    period: row.label,
    production: row.value,
    collections: collectionByLabel.get(row.label) ?? 0,
  }))
}

export const percentOf = (numerator: number, denominator: number): number => {
  if (denominator === 0) return 0
  return Math.round((numerator / denominator) * 1000) / 10
}

export const toRelativePercentPair = (
  production: number,
  collections: number,
): { production: number; collections: number } => {
  const basis = Math.max(production, collections)
  return {
    production: percentOf(production, basis),
    collections: percentOf(collections, basis),
  }
}
